import {IconButton, ITag, Label, Stack, TagPicker} from '@fluentui/react';
import React, {useMemo, useRef, useState} from 'react';
import {TFunction, useTranslation} from 'react-i18next';
import {
	InfinityListColumn,
	InfinityListColumnFilterOptions,
	InfinityListFilter,
} from './InfinityList';
import {v4 as uuidv4} from 'uuid';
import {LocalizedDatePicker} from 'components/hookForms';
import {formatDateTime} from 'i18n/localeDateFormat';
import i18n from 'i18n/i18n';

export type AdvancedFilterTreeNode = {
	isLogicalNode?: boolean;
	isFilterNode?: boolean;
	logicalOperator?: string;
	filter?: InfinityListFilter;
	parentIndex?: string;
	currentIndex: string;
};

type AdvancedFilterNodeMetadata = {
	hierarchyLevel: number;
	conditionNumber: string;
	currentPos: number;
	neigbourPos: number;
};

type AdvancedFilterNodeProps = {
	columns: InfinityListColumn[];
	columnFilterOptions?: InfinityListColumnFilterOptions[];
	node?: AdvancedFilterTreeNode;
	nodeMetaData?: AdvancedFilterNodeMetadata;
	advancedFilterNodeList: AdvancedFilterTreeNode[];
	setadvancedFilterNodeList: (
		advancedFilterNodeList: AdvancedFilterTreeNode[],
	) => void;
};

type AdvancedFilterTreeProps = {
	columns: InfinityListColumn[];
	columnFilterOptions?: InfinityListColumnFilterOptions[];
	advancedFilterNodeList: AdvancedFilterTreeNode[];
	setAdvancedFilterNodeList: (
		advancedFilterNodeList: AdvancedFilterTreeNode[],
	) => void;
};

const InfinityListAdvancedFilterTree: React.FC<AdvancedFilterTreeProps> = ({
	columns,
	columnFilterOptions,
	advancedFilterNodeList,
	setAdvancedFilterNodeList,
}) => {
	const nodeMetaDataList: AdvancedFilterNodeMetadata[] = useMemo(
		() => determineNodeMetaData(advancedFilterNodeList),
		[advancedFilterNodeList],
	);

	return (
		<Stack>
			{advancedFilterNodeList.length > 0 ? (
				advancedFilterNodeList.map((node, index) => (
					<InfinityListAdvancedFilterNode
						key={uuidv4()}
						columns={columns}
						columnFilterOptions={columnFilterOptions}
						node={node}
						nodeMetaData={nodeMetaDataList.at(index)}
						advancedFilterNodeList={advancedFilterNodeList}
						setadvancedFilterNodeList={setAdvancedFilterNodeList}
					/>
				))
			) : (
				<InfinityListAdvancedFilterNode
					columns={columns}
					advancedFilterNodeList={advancedFilterNodeList}
					setadvancedFilterNodeList={setAdvancedFilterNodeList}
				/>
			)}
		</Stack>
	);
};

const InfinityListAdvancedFilterNode: React.FC<AdvancedFilterNodeProps> = ({
	columns,
	columnFilterOptions,
	node,
	nodeMetaData,
	advancedFilterNodeList,
	setadvancedFilterNodeList,
}) => {
	const {t} = useTranslation('components/infinitylist', {
		keyPrefix: 'AdvancedFilter',
	});

	const {
		logicalOperatorSuggestions,
		arrayOperationSuggestions,
		dateOperationSuggestions,
	} = getStandardSuggestions(t);

	const parentIndex = node ? node.parentIndex : undefined;
	const level = nodeMetaData?.hierarchyLevel ?? 0;
	const currentIndex = node ? node.currentIndex : uuidv4();

	const isadvancedFilterNodeListEmpty = advancedFilterNodeList.length === 0;

	const {isColumnKeyNeeded, isOperationNeeded, isSelectionNeeded} =
		determineUserRequiredInput(node);

	const {tagPickerNeeded, datePickerNeeded} = determinePickerType(
		columns,
		isColumnKeyNeeded,
		isOperationNeeded,
		isSelectionNeeded,
		node,
	);

	const {placeholder, conditionText} = determineHints(
		columns,
		logicalOperatorSuggestions,
		arrayOperationSuggestions,
		dateOperationSuggestions,
		isadvancedFilterNodeListEmpty,
		isColumnKeyNeeded,
		isOperationNeeded,
		isSelectionNeeded,
		t,
		columnFilterOptions,
		node,
	);

	const handleResolveSuggestions = (
		filterText: string,
		selectedItems?: ITag[] | undefined,
	): ITag[] => {
		return determineSuggestions(
			columns,
			logicalOperatorSuggestions,
			arrayOperationSuggestions,
			dateOperationSuggestions,
			isadvancedFilterNodeListEmpty,
			isColumnKeyNeeded,
			isOperationNeeded,
			isSelectionNeeded,
			columnFilterOptions,
			node,
		);
	};

	const handleEmptyResolveSuggestion = (
		selectedItems?: ITag[] | undefined,
	): ITag[] => {
		return determineSuggestions(
			columns,
			logicalOperatorSuggestions,
			arrayOperationSuggestions,
			dateOperationSuggestions,
			isadvancedFilterNodeListEmpty,
			isColumnKeyNeeded,
			isOperationNeeded,
			isSelectionNeeded,
			columnFilterOptions,
			node,
		);
	};

	const handleItemSelected = (selectedItem?: ITag | undefined) => {
		if (selectedItem) {
			const isLogicalNode =
				logicalOperatorSuggestions.find(x => x.key === selectedItem.key) !==
				undefined;

			const selectedKey = selectedItem.key as string;
			const selectedValue = selectedItem.name as string;
			const nodeFilter = node?.filter;
			const selectedNode: AdvancedFilterTreeNode = isLogicalNode
				? {
						currentIndex,
						parentIndex,
						isLogicalNode: true,
						logicalOperator: selectedKey,
				  }
				: {
						currentIndex,
						parentIndex,
						isFilterNode: true,
						filter: {
							columnKey: isColumnKeyNeeded
								? selectedKey
								: nodeFilter?.columnKey ?? '',
							operation: isOperationNeeded
								? selectedKey
								: nodeFilter?.operation ?? '',
							selection: isSelectionNeeded
								? selectedKey
								: nodeFilter?.selection ?? '',
							display: isSelectionNeeded
								? selectedValue
								: nodeFilter?.display ?? '',
						},
				  };

			if (isadvancedFilterNodeListEmpty) {
				setadvancedFilterNodeList([selectedNode]);
			} else {
				setadvancedFilterNodeList(
					advancedFilterNodeList.map(x => {
						return x.currentIndex === currentIndex ? selectedNode : x;
					}),
				);
			}
			return selectedItem;
		}
		return null;
	};

	const handleDateSelected = (date: Date | null | undefined) => {
		if (date) {
			setadvancedFilterNodeList(
				advancedFilterNodeList.map(x => {
					return x.currentIndex === currentIndex
						? {
								currentIndex,
								parentIndex,
								level,
								isFilterNode: true,
								filter: {
									columnKey: node?.filter?.columnKey ?? '',
									operation: node?.filter?.operation ?? '',
									selection: date.toISOString(),
									display: formatDateTime(date, i18n),
								},
						  }
						: x;
				}),
			);
		}
	};

	const handleAddNode = () => {
		const currentNodePos = nodeMetaData?.currentPos ?? -1;
		const nextNeighbourNodePos = nodeMetaData?.neigbourPos ?? -1;
		const lastChildNodePos =
			nextNeighbourNodePos === -1
				? advancedFilterNodeList.length + 1
				: nextNeighbourNodePos;
		setadvancedFilterNodeList([
			...advancedFilterNodeList.slice(0, lastChildNodePos),
			{
				parentIndex: currentIndex,
				currentIndex: uuidv4(),
			},
			...advancedFilterNodeList.slice(lastChildNodePos),
		]);
	};

	const handleDeleteNode = () => {
		const currentNodePos = nodeMetaData?.currentPos ?? -1;
		const nextNeighbourNodePos = nodeMetaData?.neigbourPos ?? -1;
		const lastChildNodePos =
			nextNeighbourNodePos === -1
				? advancedFilterNodeList.length + 1
				: nextNeighbourNodePos;

		setadvancedFilterNodeList([
			...advancedFilterNodeList.slice(0, currentNodePos),
			...advancedFilterNodeList.slice(lastChildNodePos),
		]);
	};

	const handleEditNode = () => {
		const defaultNode: AdvancedFilterTreeNode = {
			...node!,
			filter: undefined,
			logicalOperator: undefined,
		};
		const advancedFilterNodeListNew = advancedFilterNodeList.map(x =>
			x.currentIndex === node?.currentIndex ? defaultNode : x,
		);
		setadvancedFilterNodeList(advancedFilterNodeListNew);
	};

	return (
		<Stack horizontal tokens={{childrenGap: 5}} horizontalAlign='space-between'>
			<Stack.Item styles={{root: {paddingLeft: 10 * level, width: '100%'}}}>
				{node && (
					<div>
						{nodeMetaData?.conditionNumber ?? ''}
						{conditionText.length > 0 ? ' ' + conditionText : ''}
					</div>
				)}
				{tagPickerNeeded && (
					<TagPicker
						onResolveSuggestions={handleResolveSuggestions}
						onEmptyResolveSuggestions={handleEmptyResolveSuggestion}
						pickerSuggestionsProps={{
							suggestionsHeaderText: t('Condition'),
							noResultsFoundText: t('NoResult'),
						}}
						onItemSelected={handleItemSelected}
						inputProps={{
							placeholder,
						}}
					/>
				)}
				{datePickerNeeded && (
					<LocalizedDatePicker
						placeholder={t('DatePicker')}
						onSelectDate={handleDateSelected}
					/>
				)}
			</Stack.Item>
			<Stack.Item>
				<Stack horizontal>
					{node && node.isLogicalNode && (
						<IconButton iconProps={{iconName: 'Add'}} onClick={handleAddNode} />
					)}
					{node && (
						<>
							<IconButton
								iconProps={{iconName: 'Edit'}}
								onClick={handleEditNode}
							/>
							<IconButton
								iconProps={{iconName: 'Delete'}}
								onClick={handleDeleteNode}
							/>
						</>
					)}
				</Stack>
			</Stack.Item>
		</Stack>
	);
};

const determineNodeMetaData = (
	advancedFilterNodeList: AdvancedFilterTreeNode[],
): AdvancedFilterNodeMetadata[] => {
	const nodeMetaDataList: AdvancedFilterNodeMetadata[] = [];

	advancedFilterNodeList.forEach((node, index) => {
		let hierarchyLevel = 0;
		let conditionNumber = '1.';
		let neigbourPos = -1;

		if (node.parentIndex) {
			const parentId = advancedFilterNodeList.findIndex(
				x => x.currentIndex === node.parentIndex,
			);
			const siblings = advancedFilterNodeList.filter(
				x => x.parentIndex === node.parentIndex,
			);
			const parentMetaData = nodeMetaDataList.at(parentId);
			if (parentMetaData) {
				hierarchyLevel = (parentMetaData.hierarchyLevel ?? 0) + 1;
				const currentIdWRTSiblings = siblings.findIndex(
					x => x.currentIndex === node.currentIndex,
				);
				conditionNumber =
					parentMetaData.conditionNumber + (currentIdWRTSiblings + 1) + '.';

				const neigbour = siblings.at(currentIdWRTSiblings + 1);
				if (neigbour) {
					neigbourPos = advancedFilterNodeList.findIndex(
						x => x.currentIndex === neigbour.currentIndex,
					);
				}
			}
		}

		nodeMetaDataList.push({
			hierarchyLevel,
			conditionNumber,
			currentPos: index,
			neigbourPos,
		});
	});
	return nodeMetaDataList;
};

const getStandardSuggestions = (
	t: TFunction,
): {
	logicalOperatorSuggestions: ITag[];
	arrayOperationSuggestions: ITag[];
	dateOperationSuggestions: ITag[];
} => {
	const logicalOperatorSuggestions: ITag[] = [
		{
			key: 'and',
			name: t('AND'),
		},
		{
			key: 'or',
			name: t('OR'),
		},
	];

	const arrayOperationSuggestions: ITag[] = [
		{
			key: 'eq',
			name: t('EQ'),
		},
		{
			key: 'neq',
			name: t('NEQ'),
		},
	];

	const dateOperationSuggestions: ITag[] = [
		{
			key: 'lte',
			name: t('LTE_date'),
		},
		{
			key: 'gte',
			name: t('GTE_date'),
		},
	];

	return {
		logicalOperatorSuggestions,
		arrayOperationSuggestions,
		dateOperationSuggestions,
	};
};

const determineUserRequiredInput = (
	node?: AdvancedFilterTreeNode,
): {
	isColumnKeyNeeded: boolean;
	isOperationNeeded: boolean;
	isSelectionNeeded: boolean;
} => {
	const isColumnKeyNeeded =
		node === undefined ||
		(node.isFilterNode === undefined && node.isLogicalNode === undefined) ||
		(node.isFilterNode === true && node.filter === undefined) ||
		(node.isLogicalNode === true && node.logicalOperator === undefined);

	const isOperationNeeded =
		(node &&
			node.filter &&
			node.filter.columnKey !== '' &&
			node.filter.operation === '') ??
		false;

	const isSelectionNeeded =
		(node &&
			node.filter &&
			node.filter.columnKey !== '' &&
			node.filter.operation !== '' &&
			node.filter.selection === '') ??
		false;

	return {isColumnKeyNeeded, isOperationNeeded, isSelectionNeeded};
};

const determinePickerType = (
	columns: InfinityListColumn[],
	isColumnKeyNeeded: boolean,
	isOperationNeeded: boolean,
	isSelectionNeeded: boolean,
	node?: AdvancedFilterTreeNode,
): {tagPickerNeeded: boolean; datePickerNeeded: boolean} => {
	const tagPickerNeeded =
		isColumnKeyNeeded ||
		isOperationNeeded ||
		(isSelectionNeeded &&
			columns.find(x => x.key === node?.filter?.columnKey)?.filterType ===
				'Array');

	const datePickerNeeded =
		isSelectionNeeded &&
		columns.find(x => x.key === node?.filter?.columnKey)?.filterType === 'Date';

	return {tagPickerNeeded, datePickerNeeded};
};

const determineHints = (
	columns: InfinityListColumn[],
	logicalOperatorSuggestions: ITag[],
	arrayOperationSuggestions: ITag[],
	dateOperationSuggestions: ITag[],
	isadvancedFilterNodeListEmpty: boolean,
	isColumnKeyNeeded: boolean,
	isOperationNeeded: boolean,
	isSelectionNeeded: boolean,
	t: TFunction,
	columnFilterOptions?: InfinityListColumnFilterOptions[],
	node?: AdvancedFilterTreeNode,
): {placeholder: string; conditionText: string} => {
	let placeholder = '';
	let conditionText = '';

	if (isadvancedFilterNodeListEmpty) {
		placeholder = t('ConditionPicker');
	} else if (isColumnKeyNeeded) {
		placeholder = t('ConditionPickerBoth');
	}

	if (node && node.isLogicalNode) {
		conditionText =
			logicalOperatorSuggestions.find(x => x.key === node.logicalOperator)
				?.name ?? '';
	}

	if (node && node.isFilterNode) {
		const operatorTag = [
			...arrayOperationSuggestions,
			...dateOperationSuggestions,
		].find(x => x.key === node.filter?.operation);

		const columnKey = node.filter?.columnKey;
		const col = columns.find(x => x.key === columnKey);
		const colFilterType = col?.filterType;
		if (colFilterType === 'Array') {
			const valueField = (
				columnFilterOptions?.find(x => x.key === col?.key)?.fieldValuesUnique ??
				[]
			).find(x => x.optionKey === node.filter?.selection);

			conditionText = `${col?.name ?? ''}${
				operatorTag ? ' ' + operatorTag.name : ''
			}${valueField ? ' ' + valueField.name : ''}`;
		} else {
			const valueField = node.filter?.selection;

			conditionText = `${col?.name ?? ''}${
				operatorTag ? ' ' + operatorTag.name : ''
			}${
				node.filter?.selection
					? ' ' + formatDateTime(new Date(node.filter?.selection), i18n)
					: ''
			}`;
		}
	}

	return {placeholder, conditionText};
};

const determineSuggestions = (
	columns: InfinityListColumn[],
	logicalOperatorSuggestions: ITag[],
	arrayOperationSuggestions: ITag[],
	dateOperationSuggestions: ITag[],
	isadvancedFilterNodeListEmpty: boolean,
	isColumnKeyNeeded: boolean,
	isOperationNeeded: boolean,
	isSelectionNeeded: boolean,
	columnFilterOptions?: InfinityListColumnFilterOptions[],
	node?: AdvancedFilterTreeNode,
): ITag[] => {
	const columnSuggestions: ITag[] = columns
		.filter(x => x.filterType === 'Array' || x.filterType === 'Date')
		.map((val, j) => ({
			key: val.key,
			name: val.name,
		}));

	if (isadvancedFilterNodeListEmpty) {
		return logicalOperatorSuggestions;
	}

	if (isColumnKeyNeeded) {
		if (node?.isLogicalNode === true) {
			return [...logicalOperatorSuggestions];
		}
		if (node?.isFilterNode === true) {
			return [...columnSuggestions];
		}
		return [...logicalOperatorSuggestions, ...columnSuggestions];
	}

	if (isOperationNeeded) {
		const columnKey = node?.filter?.columnKey;
		const col = columns.find(x => x.key === columnKey);
		const filterType = col?.filterType ?? '';
		if (filterType === 'Array') {
			return [...arrayOperationSuggestions];
		}
		if (filterType === 'Date') {
			return [...dateOperationSuggestions];
		}
	}

	if (isSelectionNeeded) {
		const columnKey = node?.filter?.columnKey;
		const col = columns.find(x => x.key === columnKey);
		const fieldValues = (
			columnFilterOptions?.find(x => x.key === col?.key)?.fieldValuesUnique ??
			[]
		).slice(0, 50);
		return fieldValues.map(x => {
			return {key: x.optionKey, name: x.name};
		});
	}

	return [];
};

export const determineDefaultAdvancedFilterTree = (
	allListFilters: InfinityListFilter[],
	columns: InfinityListColumn[],
): AdvancedFilterTreeNode[] => {
	const nodeListNew: AdvancedFilterTreeNode[] = [];

	const filteredColumns = [...new Set(allListFilters.map(x => x.columnKey))];

	const arrayFilterFields = columns
		.filter(x => x.filterType === 'Array')
		.map(x => x.key);
	const dateFilterFields = columns
		.filter(x => x.filterType === 'Date')
		.map(x => x.key);

	const rootNodeIndex = uuidv4();
	const isMultipleConditions = filteredColumns.length >= 2;
	if (isMultipleConditions) {
		nodeListNew.push({
			currentIndex: rootNodeIndex,
			parentIndex: undefined,
			isLogicalNode: true,
			logicalOperator: 'and',
		});
	}

	filteredColumns.forEach(x1 => {
		const lstOptions = allListFilters.filter(x2 => x2.columnKey === x1);
		if (lstOptions.length > 0) {
			const columnRootIndex = uuidv4();
			nodeListNew.push({
				currentIndex: columnRootIndex,
				parentIndex: isMultipleConditions ? rootNodeIndex : undefined,
				isLogicalNode: true,
				logicalOperator: dateFilterFields.includes(x1) ? 'and' : 'or',
			});

			lstOptions.forEach(x2 => {
				nodeListNew.push({
					currentIndex: uuidv4(),
					parentIndex: columnRootIndex,
					isFilterNode: true,
					filter: x2,
				});
			});
		}
	});

	return nodeListNew;
};

export default InfinityListAdvancedFilterTree;
